import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Select, Spin } from "antd";
import moment from "moment";
import MySegmented from "../../components/segmented/Segmented";
import SkeletonTable from "../../components/table/SkeletonTable";
import columns from "../../columns/files/viewFile";
import View from "../../assets/View";
import Spinner from "../../components/loader/Spinner";
import Delete from "../../assets/Delete";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import {
  AssignTaskApi,
  DeleteServiceApi,
  GetEmployeeList,
  GetServiceTaskApi,
  GetTaskWiseFileApi,
} from "../../api/request/service";
import BackButton from "../../components/button/BackButton";
import UserAvatar from "../../components/common/UserAvatar";
import Search from "../../components/common/Search";
import GlobalTooltip from "../../components/common/GlobalTooltip";

const ViewFile = () => {
  const { fileTypeId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [service, setService] = useState("");
  const [taskList, setTaskList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [segmentOptions, setSegmentOptions] = useState([]);
  const [segmentedValue, setSegmentedValue] = useState(state?.task || "");
  const [fileList, setFileList] = useState([]);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [employeeList, setEmployeeList] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFile, setSelectedFile] = useState({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const getServiceTaskList = async () => {
    try {
      setIsLoading(true);
      const params = { service_type_id: fileTypeId };
      const { data } = await GetServiceTaskApi(params);
      setService(data?.service_name);
      if (data?.status && data?.data?.length) {
        setTaskList(data?.data);
        const options = data?.data?.map((item) => {
          return {
            label: `${item.name} (${item?.total_file_count})`,
            value: item?.name,
          };
        });
        setSegmentOptions(options);
        if (!segmentedValue) setSegmentedValue(options[0]?.value);
      }
      setIsLoading(false);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
      setIsLoading(false);
    }
  };

  const getEmployeeData = () => {
    const task = taskList?.find((task) => task?.name === segmentedValue);
    const employeeData = employeeList
      .filter((item) =>
        item?.permissions?.some((item) => item?.id === task?.id)
      )
      .map((item) => {
        return { label: item?.name, value: item?.id };
      });
    return employeeData;
  };

  const handleView = (data) => {
    const task = taskList?.find((task) => task?.name === segmentedValue);
    const isLastTask = taskList[taskList.length - 1]?.orders === task?.orders;
    navigate(`/file/${fileTypeId}/${data?.id}`, {
      state: { client: data?.user, task, isLastTask },
    });
  };

  const getFileList = async (searchText) => {
    try {
      setIsFileLoading(true);
      const task = taskList?.find((item) => item?.name === segmentedValue);
      const params = {
        task_id: task?.id,
        service_type_id: fileTypeId,
        page: currentPage,
        search: searchText,
      };
      const { data } = await GetTaskWiseFileApi(params);
      const employeeData = getEmployeeData();
      if (data?.status) {
        setFileList(
          data?.data?.map((item) => {
            const assignTo = employeeData?.find(
              (emp) => emp?.value === item?.employee_id
            );
            return {
              ...item,
              userDetails: <UserAvatar data={item?.user} />,
              file: (
                <div className="font-bold highlight-text capitalize">
                  {item?.service_name_slug}
                </div>
              ),
              submit: item?.is_submit ? "Yes" : "No",
              assignTo: (
                <div onClick={(e) => e.stopPropagation()}>
                  <Select
                    placeholder="Select employee"
                    style={{ width: "100%" }}
                    value={assignTo?.value || item?.employee_name}
                    options={employeeData}
                    onChange={(value) =>
                      assignHandler(item?.id, value, item?.employee_id)
                    }
                  />
                </div>
              ),
              startDate: item?.created_at
                ? moment(item?.created_at).format("lll")
                : "",
              action: (
                <div
                  className="flex items-center gap-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  <GlobalTooltip title={"View File"}>
                    <button onClick={() => handleView(item)}>
                      <View />
                    </button>
                  </GlobalTooltip>
                  <GlobalTooltip title={"Archive File"}>
                    <button
                      onClick={() => {
                        setSelectedFile(item);
                        setIsDeleteModalOpen(true);
                      }}
                    >
                      <Delete />
                    </button>
                  </GlobalTooltip>
                </div>
              ),
            };
          })
        );
        setTotal(data?.total_files);
        setTotalRecords(data?.total_records);
      }
      setIsFileLoading(false);
    } catch (error) {
      setFileList([]);
      setIsFileLoading(false);
    }
  };

  const assignHandler = async (id, employee_id, currentEmployeeId) => {
    if (employee_id === currentEmployeeId) return;
    try {
      setFileList((prev) => {
        return prev.map((item) =>
          item.id === id ? { ...item, assignTo: <Spinner /> } : item
        );
      });

      let params = { file_id: id, employee_id: employee_id };
      const { data } = await AssignTaskApi(params);
      if (data?.status) setAssignData(id, employee_id);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
      setAssignData(id, currentEmployeeId);
    }
  };

  const getEmployeeList = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetEmployeeList();
      if (data.status) {
        setEmployeeList(data?.data);
        columns.map((item) => {
          if (item.dataIndex === "assignTo") {
            item.filters = data?.data?.map((item) => {
              return { text: item?.name, value: item?.id };
            });
          }
        });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getEmployeeList();
  }, []);

  useEffect(() => {
    if (fileTypeId) getServiceTaskList();
  }, [fileTypeId]);

  useEffect(() => {
    if (taskList?.length) getFileList();
  }, [segmentedValue, taskList, employeeList, currentPage]);

  const setAssignData = (id, employeeId) => {
    const employeeData = getEmployeeData();
    setFileList((prev) => {
      return prev.map((item) =>
        item.id === id
          ? {
              ...item,
              employee_id: employeeId,
              assignTo: (
                <div onClick={(e) => e.stopPropagation()}>
                  <Select
                    placeholder="Select employee"
                    style={{ width: "100%" }}
                    value={employeeId}
                    options={employeeData}
                    onChange={(value) =>
                      assignHandler(item?.id, value, employeeId)
                    }
                  />
                </div>
              ),
            }
          : item
      );
    });
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination?.current);
  };

  const deleteFile = async () => {
    if (!selectedFile?.id) return;
    try {
      setIsDeleteLoading(true);
      const { data } = await DeleteServiceApi({ file_id: selectedFile?.id });
      if (data?.status) {
        toast.success(data?.message);
        setIsDeleteModalOpen(false);
        setSelectedFile({});
        getServiceTaskList();
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  return (
    <>
      <div className="border rounded-md bg-white">
        <div className="p-5">
          <div className="flex items-center gap-3">
            <BackButton />
            <h2 className="title">
              {service} {totalRecords ? `(${totalRecords})` : null}
            </h2>
          </div>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center gap-2 my-16">
            <Spin />
            <p className="text-secondaryLight1">Loading...</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {taskList?.length ? (
              <>
                <div className="md:flex items-center md:justify-between px-3">
                  <div className="overflow-x-auto">
                    <MySegmented
                      options={segmentOptions}
                      value={segmentedValue}
                      setValue={setSegmentedValue}
                    />
                  </div>
                  <div className="w-48 ml-auto md:mt-0 mt-4">
                    <Search handleSearch={getFileList} />
                  </div>
                </div>

                <div className="table-radius-0 text-light mb-4">
                  <SkeletonTable
                    columns={columns}
                    data={fileList}
                    isLoading={isFileLoading}
                    pagination={{
                      current: currentPage,
                      total: total,
                      pageSize: 10,
                      showSizeChanger: false,
                    }}
                    handleTableChange={handleTableChange}
                    onRowClick={(data) => handleView(data)}
                  />
                </div>
              </>
            ) : (
              <p className="text-center text-Gray my-16">No task available</p>
            )}
          </div>
        )}
      </div>

      {/* --- Delete confirmation modal --- */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        message={`Are you sure you want to archive this file?`}
        onConfirm={deleteFile}
        isLoading={isDeleteLoading}
        loadingLabel={`Deleting file...`}
      />
    </>
  );
};

export default ViewFile;
